import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { mediaSrc } from "../lib/media";
import { formatDate } from "../lib/i18n";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import DemoBadge from "../components/DemoBadge";
import { MediaTile, EmptyState, Loader } from "../components/Blocks";

export default function ChallengeDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/challenges/${slug}`).then(({ data }) => setC(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <EmptyState message="Challenge introuvable." testid="challenge-not-found" />;
  if (!c) return <Loader />;

  return (
    <div data-testid="challenge-detail-page">
      <Seo
        title={c.title}
        description={(c.description || "").slice(0, 180)}
        path={`/challenges/${c.slug}`}
        image={c.hero_media_url}
        kind="challenge"
        type="article"
      />
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        {c.hero_media_url && (
          <img src={mediaSrc(c.hero_media_url)} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
        )}
        <div className="absolute inset-0 vb-scrim" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pb-14 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="vb-label !text-[#FF2E63]">{c.status === "open" ? "En cours" : "Fermé"}</span>
            <DemoBadge show={c.is_demo} />
          </div>
          <h1 className="vb-display text-4xl lg:text-6xl font-black" data-testid="challenge-title">
            {c.title}
          </h1>
          <p className="mt-4 text-sm text-neutral-300">
            {formatDate(c.start_date)} → {formatDate(c.end_date)}
          </p>
          <div className="mt-6">
            <ShareButton
              path={`/challenges/${c.slug}`}
              title={`${c.title} — VytaliBoots`}
              text="Découvrez ce challenge sur VytaliBoots."
              testid="challenge-share-button"
            />
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <p className="text-base text-neutral-300 leading-relaxed max-w-3xl">{c.description}</p>
          <div>
            <div className="vb-label mb-3">Règles</div>
            <p className="text-sm text-neutral-400 max-w-3xl whitespace-pre-line">{c.rules}</p>
          </div>
          <div>
            <div className="vb-label mb-6">Participations vérifiées</div>
            {(c.entries || []).length === 0 ? (
              <EmptyState message="Aucune participation vérifiée pour le moment." testid="challenge-entries-empty" />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="challenge-entries">
                {c.entries.map((e, i) => (
                  <div key={e.id} className="vb-card overflow-hidden" data-testid={`challenge-entry-${i}`}>
                    {e.media ? (
                      <MediaTile item={e.media} testid={`challenge-entry-media-${i}`} className="aspect-square" />
                    ) : (
                      <div className="aspect-square grid place-items-center text-neutral-700 text-xs">Publication externe</div>
                    )}
                    <div className="p-4">
                      <div className="text-sm">{e.profile?.display_name || "Participant"}</div>
                      <div className="text-xs text-neutral-500 mt-1">
                        {e.status === "winner" ? "Gagnant" : "Vérifié"}
                        {e.manually_entered_engagement_count != null
                          ? ` · ${e.manually_entered_engagement_count} interactions déclarées`
                          : ""}
                      </div>
                      <DemoBadge show={e.is_demo} className="mt-3" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <aside className="space-y-8">
          {c.event && (
            <div className="vb-card p-6">
              <div className="vb-label mb-3">Événement éligible</div>
              <div className="vb-display text-lg">{c.event.name}</div>
              <Link to={`/evenements/${c.event.slug}`} data-testid="challenge-event-link" className="vb-btn vb-btn-ghost mt-6 w-full">
                Voir l'événement
              </Link>
            </div>
          )}
          <div className="vb-card p-6">
            <div className="vb-label mb-3">Participer</div>
            <p className="text-sm text-neutral-400">
              Les membres connectés peuvent soumettre un contenu approuvé ou un lien de publication
              depuis leur compte.
            </p>
            <Link to="/compte" data-testid="challenge-participate-link" className="vb-btn vb-btn-primary mt-6 w-full">
              Mon compte
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
